import React, { useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import styled from 'styled-components';

import type { PatientChatFeedbackRating } from '@mummssoftware/utils';

import type { AnswerFeedback as Feedback } from '../../types';
import defaults from '../../translations/en.json';

/** The gateway's limit. */
const COMMENT_MAX_LENGTH = 2000;

/**
 * Hidden on an older, unrated answer until the pointer or keyboard focus
 * reaches it (AnswerBlock in MessageList sets the hover); always shown on touch.
 */
const Row = styled.div<{ $quiet: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.primary};
  opacity: ${(props) => (props.$quiet ? 0 : 1)};
  transition: opacity 120ms;
  &:focus-within {
    opacity: 1;
  }
  @media (hover: none) {
    opacity: 1;
  }
`;

const Thumb = styled(IconButton)<{ $selected: boolean }>`
  && {
    padding: 4px;
    border-radius: 4px;
    color: ${(props) =>
      props.$selected
        ? props.theme.palette.primary.main
        : props.theme.palette.text.primary};
    background-color: ${(props) =>
      props.$selected
        ? props.theme.palette.background.dark1 ||
          props.theme.palette.action.selected
        : 'transparent'};
  }
`;

const LinkButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  color: ${(props) => props.theme.palette.primary.main};
  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const Sent = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.palette.success?.main || '#2e7d32'};
`;

const Failed = styled.span`
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
`;

const Panel = styled.div`
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
  && button {
    padding: 2px;
  }
`;

const Comment = styled(TextField)`
  && {
    margin-top: 6px;
  }
  && textarea {
    font-size: 0.8rem;
    line-height: 1.4;
  }
`;

const PanelActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
  && button {
    font-size: 0.75rem;
    min-width: 0;
    padding: 2px 12px;
  }
`;

type AnswerFeedbackProps = {
  feedback?: Feedback;
  /** The newest answer: controls always shown, with the prompt. */
  isLatest: boolean;
  /** Sends the rating (null undoes it); false when it was not saved. */
  onRate: (feedback: Feedback | null) => Promise<boolean>;
};

/**
 * Thumbs under a live answer: one click saves the rating; a comment is offered,
 * never required. Thumbs down opens the comment box at once.
 */
export const AnswerFeedback = ({
  feedback,
  isLatest,
  onRate,
}: AnswerFeedbackProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const rating = feedback?.rating ?? null;
  const sentComment = feedback?.comment || null;

  const save = async (next: Feedback | null) => {
    setIsSaving(true);
    setFailed(false);
    const ok = await onRate(next);
    setIsSaving(false);
    setFailed(!ok);
    return ok;
  };

  const handleThumb = async (clicked: PatientChatFeedbackRating) => {
    setDraft('');
    if (clicked === rating) {
      setIsOpen(false); // a second click undoes the rating
      await save(null);
      return;
    }
    setIsOpen(clicked === 'down');
    if (!(await save({ rating: clicked }))) {
      setIsOpen(false);
    }
  };

  const openPanel = () => {
    setDraft(sentComment || '');
    setIsOpen(true);
  };

  const handleSend = async () => {
    const comment = draft.trim();
    if (!rating || !comment) {
      return;
    }
    if (await save({ rating, comment })) {
      setIsOpen(false);
    }
  };

  let status: React.ReactNode = null;
  if (failed) {
    status = <Failed role="alert">{defaults.feedbackFailed}</Failed>;
  } else if (rating && !isOpen && sentComment) {
    status = <Sent>{`✓ ${defaults.feedbackSent}`}</Sent>;
  } else if (rating && !isOpen) {
    status = (
      <>
        <span>{defaults.feedbackThanks}</span>
        <LinkButton type="button" onClick={openPanel}>
          {defaults.feedbackAddComment}
        </LinkButton>
      </>
    );
  } else if (!rating && isLatest) {
    status = <span>{defaults.feedbackPrompt}</span>;
  }

  const isDown = rating === 'down';

  return (
    <div data-testid="maia-answer-feedback">
      <Row
        className="maia-feedback-row"
        $quiet={!isLatest && !rating && !isOpen && !failed}
      >
        <Thumb
          size="small"
          $selected={rating === 'up'}
          aria-label={defaults.feedbackRelevant}
          aria-pressed={rating === 'up'}
          title={defaults.feedbackRelevant}
          disabled={isSaving}
          onClick={() => handleThumb('up')}
        >
          {rating === 'up' ? (
            <ThumbUpIcon fontSize="small" />
          ) : (
            <ThumbUpOutlinedIcon fontSize="small" />
          )}
        </Thumb>
        <Thumb
          size="small"
          $selected={isDown}
          aria-label={defaults.feedbackNotRelevant}
          aria-pressed={isDown}
          title={defaults.feedbackNotRelevant}
          disabled={isSaving}
          onClick={() => handleThumb('down')}
        >
          {isDown ? (
            <ThumbDownIcon fontSize="small" />
          ) : (
            <ThumbDownOutlinedIcon fontSize="small" />
          )}
        </Thumb>
        {status}
      </Row>
      <Collapse
        in={isOpen && Boolean(rating)}
        unmountOnExit
        onEntered={() =>
          panelRef.current?.scrollIntoView?.({ block: 'nearest' })
        }
      >
        <Panel
          ref={panelRef}
          role="group"
          aria-label={
            isDown
              ? defaults.feedbackWhatWasWrong
              : defaults.feedbackLeaveComment
          }
        >
          <PanelHeader>
            <span>
              {isDown
                ? defaults.feedbackWhatWasWrong
                : defaults.feedbackLeaveComment}
            </span>
            <IconButton
              size="small"
              aria-label={defaults.feedbackClose}
              onClick={() => setIsOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </PanelHeader>
          <Comment
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            variant="outlined"
            size="small"
            autoFocus
            placeholder={
              isDown
                ? defaults.feedbackWrongPlaceholder
                : defaults.feedbackCommentPlaceholder
            }
            inputProps={{
              maxLength: COMMENT_MAX_LENGTH,
              'aria-label': defaults.feedbackComment,
            }}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <PanelActions>
            <Button size="small" onClick={() => setIsOpen(false)}>
              {defaults.feedbackSkip}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={isSaving || !draft.trim()}
              onClick={handleSend}
            >
              {defaults.send}
            </Button>
          </PanelActions>
        </Panel>
      </Collapse>
    </div>
  );
};

export default AnswerFeedback;
